import React, { useState, useEffect } from "react";
import { getAllBalances, adjustBalance, getAllTransactions } from "../lib/balance";
import { getAllCustomers } from "../lib/users";
import { Modal, toast, formatDate } from "./UI";

export default function BalanceModal({ onClose }) {
  const [customers,     setCustomers]     = useState([]);
  const [balances,      setBalances]      = useState([]);
  const [transactions,  setTransactions]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selEmail,      setSelEmail]      = useState("");
  const [amount,        setAmount]        = useState("");
  const [note,          setNote]          = useState("");
  const [type,          setType]          = useState("topup");
  const [saving,        setSaving]        = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [custs, bals, txns] = await Promise.all([getAllCustomers(), getAllBalances(), getAllTransactions()]);
      setCustomers(custs); setBalances(bals); setTransactions(txns);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const getBalance = (email) => {
    const b = balances.find(b => b.email === email.toLowerCase());
    return b ? b.amount : 0;
  };

  const save = async () => {
    if (!selEmail || !amount) { toast("Select customer and enter amount.", "error"); return; }
    const adj = type === "charge" ? -Math.abs(parseFloat(amount)) : Math.abs(parseFloat(amount));
    setSaving(true);
    try {
      await adjustBalance(selEmail, adj, note, type);
      toast("Balance updated!");
      setAmount(""); setNote("");
      load();
    } catch (e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const custTxns = selEmail ? transactions.filter(t => t.email === selEmail.toLowerCase()) : [];

  return (
    <Modal title="Account balances" onClose={onClose} maxWidth={580}>
      {/* Adjust balance form */}
      <div style={{ background: "var(--bg-soft)", borderRadius: "var(--radius)", padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Adjust balance</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          <select value={selEmail} onChange={e => setSelEmail(e.target.value)} style={{ flex: 2, minWidth: 180 }}>
            <option value="">Select customer...</option>
            {customers.map(c => <option key={c.uid} value={c.email}>{c.name} — ${getBalance(c.email).toFixed(2)}</option>)}
          </select>
          <select value={type} onChange={e => setType(e.target.value)} style={{ width: "auto" }}>
            <option value="topup">+ Top up</option>
            <option value="charge">− Charge</option>
            <option value="adjustment">Adjustment</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount ($)" style={{ flex: 1 }} />
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note..." style={{ flex: 2 }} />
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "..." : "Apply"}</button>
        </div>
      </div>

      {/* All balances */}
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>All balances</div>
      {loading ? <div style={{ textAlign: "center", padding: "1rem", color: "var(--text-muted)" }}>Loading...</div> : (
        <div style={{ border: "0.5px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden", marginBottom: 16 }}>
          {customers.map((c, i) => {
            const bal = getBalance(c.email);
            return (
              <div key={c.uid} onClick={() => setSelEmail(c.email)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: i < customers.length - 1 ? "0.5px solid var(--border)" : "none", cursor: "pointer", background: selEmail === c.email ? "var(--green-light)" : "var(--bg)" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{c.email}</div>
                </div>
                <span style={{ fontSize: 16, fontWeight: 700, color: bal > 0 ? "var(--green-dark)" : bal < 0 ? "var(--red-dark)" : "var(--text-muted)" }}>${bal.toFixed(2)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Transaction history for selected customer */}
      {selEmail && custTxns.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Transaction history — {selEmail}</div>
          <div style={{ maxHeight: 200, overflowY: "auto", border: "0.5px solid var(--border)", borderRadius: "var(--radius)" }}>
            {custTxns.map(t => (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 14px", borderBottom: "0.5px solid var(--border)", fontSize: 13 }}>
                <div>
                  <span style={{ fontWeight: 600, color: t.amount > 0 ? "var(--green-dark)" : "var(--red-dark)" }}>{t.amount > 0 ? "+" : ""}${t.amount.toFixed(2)}</span>
                  {t.note && <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>{t.note}</span>}
                </div>
                <div style={{ color: "var(--text-faint)", fontSize: 11 }}>{formatDate(t.createdAt)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="modal-footer" style={{ marginTop: 16 }}>
        <button className="btn btn-primary" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}
